import { effect, signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { api } from "./api.js";

/**
 * Online status for friends (ADR 0031): while the app is open and visible, a signed-in person's
 * browser says so once a minute, with the game they're in. Friends see "Online" or "In Beanpair";
 * the api decides who may see it (accepted friends, not blocked, unless they appear offline).
 */

/** The game being played in a room or solo, if any. */
export const playingGame = signal<string | null>(null);

/** Marks this page as playing `game` while it's shown. */
export function usePlaying(game: string | null | undefined): void {
  useEffect(() => {
    playingGame.value = game ?? null;
    return () => {
      if (playingGame.value === (game ?? null)) playingGame.value = null;
    };
  }, [game]);
}

export const HEARTBEAT_MS = 60_000;

const beat = (game: string | null) =>
  fetch("/api/presence", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ game }),
    keepalive: true,
  }).catch(() => undefined);

let started = false;

/** Starts the heartbeat if someone is signed in here; guests send nothing. */
export function startPresence(): void {
  if (started) return;
  started = true;
  api.me().then(
    (me) => {
      if (me.signedIn !== true) return;
      const visible = () => document.visibilityState === "visible";
      // Every minute while visible, at once when the game changes or the tab comes back.
      effect(() => {
        const game = playingGame.value;
        if (visible()) void beat(game);
      });
      setInterval(() => {
        if (visible()) void beat(playingGame.value);
      }, HEARTBEAT_MS);
      document.addEventListener("visibilitychange", () => {
        if (visible()) void beat(playingGame.value);
      });
    },
    () => undefined,
  );
}
